import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import clsx from 'clsx';
import { isNull } from 'lodash';
import * as Accordion from '@radix-ui/react-accordion';
import isHotkey from 'is-hotkey';

import { getCurrentWorkspaceSelector } from '@app/reducers/app';
import {
  folderNameAlreadyExists,
  generateDuplicateName,
  getFoldersByParentId,
} from '@flow/libs/workspace/folders';
import foldersReducer from '@app/reducers/entities/folders';
import useOverflow from '@app/hooks/use-overflow';
import type { RootState } from '@app/reducers';

import { checkAcl } from '@flow/libs/utils/acl';
import Button from '@app/components/button';
import ChevronRightIcon from '@app/components/icons/chevron-right';
import { FolderActions } from '@app/components/folder-actions';
import FolderIcon from '@app/components/icons/folder';
import InlineEdit from '@app/components/inline-edit';
import SmallAdd from '@app/components/icons/small-add';
import Tooltip from '@app/components/tooltip';
import { T } from '@app/components/translation';
import WorkspaceFolder from '@app/components/icons/workspace-folder';
import ChevronDownIcon from '@app/components/icons/chevron-down';
import { useFoldersQueryReload } from '@app/components/folder-tree/use-folders-query';

import { WORKSPACE_ROOT_FOLDER_ID } from '@flow/libs/workspace/folders-constants';

import style from './folder-branch.module.scss';

type BranchType = {
  acl: Oneflow.Folder['acl'];
  allFolders: any;
  childFolders?: Oneflow.Folder[];
  contractFolderId?: number;
  customClass?: string;
  id: number;
  indentationLevel: number;
  isAddMode: boolean;
  isModalVersion?: boolean;
  isSaveFolderDestinationModal?: boolean;
  name: string;
  onAddFolder: (folderName: string, hasError?: boolean) => void;
  onFolderAddHandler: () => void;
  onSelect: (folderId: Oneflow.Folder['id']) => void;
  parentId: number | null;
  // Clean this one up after folder-tree-legacy is removed
  parents?: number[];
  selectedUniqueKey?: any;
  isReadOnly?: boolean;
  loading?: boolean;
  folderHasChildren?: boolean;
};

const FolderBranch = ({
  acl,
  allFolders,
  childFolders,
  contractFolderId,
  customClass,
  id,
  indentationLevel,
  isAddMode,
  isModalVersion = false,
  isSaveFolderDestinationModal,
  name,
  onAddFolder,
  onFolderAddHandler,
  onSelect,
  parentId,
  parents,
  selectedUniqueKey,
  isReadOnly,
  loading,
  folderHasChildren = false,
}: BranchType) => {
  const isWorkspace = id === -1;
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Memoize hasChildren to re-compute when childFolders
  const hasChildren = useMemo(
    () => childFolders?.length,
    [
      childFolders,
    ],
  );

  const currentWorkspace = useSelector((state: RootState) => getCurrentWorkspaceSelector(state));
  const nameRef = useRef(null);
  const hasOverflow = useOverflow(nameRef);
  const workspaceCreatePermission = checkAcl(currentWorkspace.acl, 'collection:folder:create');

  const dispatch = useDispatch();
  const reloadFolders = useFoldersQueryReload();

  const saveFolderName = (folderName: string) => {
    dispatch(
      foldersReducer.updateFolder({
        id,
        data: {
          name: folderName,
        },
        pipe: {
          onSuccess: reloadFolders,
        },
      }),
    );
  };

  const onEditFolderName = (newName: string) => {
    if (newName === name || hasError) {
      setIsEditMode(false);
      return;
    }
    if (newName !== '') {
      if (!folderNameAlreadyExists(newName, parentId, allFolders)) {
        saveFolderName(newName);
      } else {
        const tempName = generateDuplicateName(newName, parentId, allFolders);
        saveFolderName(tempName);
      }
    }

    setIsEditMode(false);
  };
  const isCurrentFolderSelected = selectedUniqueKey === id;

  useEffect(() => {
    // Reset error when user clicks outside of the input
    if (!isEditMode) {
      setHasError(false);
    }
  }, [
    isEditMode,
  ]);

  const getBranchContent = (content: React.ReactElement) => {
    if (contractFolderId === id && !isSaveFolderDestinationModal) {
      return (
        <Tooltip
          triggerClassName={style.FolderName}
          message={
            <T
              _str="This contract already exists in this folder."
              _comment="Tooltip message when contract already exists in folder."
            />
          }
          side="top"
          theme="oneflow"
          zIndex={10002}
        >
          {content}
        </Tooltip>
      );
    }

    return content;
  };

  const isEnterOrSpace = (e: React.KeyboardEvent) => {
    return isHotkey('enter', e) || isHotkey('space', e);
  };

  const onKeyDownHandler = (event: React.KeyboardEvent) => {
    // Don't handle keyboard events if they are same folders
    if (contractFolderId === id && !isSaveFolderDestinationModal) {
      return;
    }

    if (isEnterOrSpace(event)) {
      event.preventDefault();
      event.stopPropagation();
      setActiveFolder();
    }
  };

  // Memoize children computation to re-render when allFolders changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO: Fix this
  const memoizedChildren = useMemo(
    () =>
      childFolders?.map((branch) => (
        <ul
          className={style.List}
          key={branch.id}
        >
          <FolderBranch
            acl={branch.acl}
            allFolders={allFolders}
            childFolders={getFoldersByParentId(allFolders, branch.id)}
            contractFolderId={contractFolderId}
            id={branch.id}
            indentationLevel={indentationLevel + 1}
            isAddMode={isAddMode}
            isModalVersion={isModalVersion}
            isSaveFolderDestinationModal={isSaveFolderDestinationModal}
            name={branch.name ?? ''}
            onAddFolder={onAddFolder}
            onFolderAddHandler={onFolderAddHandler}
            onSelect={onSelect}
            parentId={id}
            parents={parents}
            selectedUniqueKey={selectedUniqueKey}
            isReadOnly={isReadOnly}
            folderHasChildren={Boolean(branch.childCount && branch.childCount > 0)}
          />
        </ul>
      )),
    [
      childFolders,
      allFolders,
      indentationLevel,
      isAddMode,
      isEditMode,
      isModalVersion,
      isSaveFolderDestinationModal,
      onAddFolder,
      onFolderAddHandler,
      onSelect,
      parents,
      selectedUniqueKey,
      isReadOnly,
      contractFolderId,
      id,
    ],
  );

  const getChildren = () => memoizedChildren;

  const didClickRename = () => {
    setIsEditMode(true);
  };

  const setActiveFolder = () => {
    onSelect(id);
  };

  const getFolderName = () => {
    if (isEditMode) {
      return (
        <InlineEdit
          setHasError={setHasError}
          onEditFolderHandler={(value) => {
            if (hasError) {
              return;
            }

            onEditFolderName(value);
          }}
          onBlurHandler={() => setIsEditMode(false)}
          value={name}
        />
      );
    }

    const folderNameElement = (
      <span
        className={
          contractFolderId !== id || isSaveFolderDestinationModal
            ? style.FolderName
            : style.FolderNameDisabled
        }
        ref={nameRef}
        role="button"
      >
        {name}
      </span>
    );

    if (hasOverflow && contractFolderId !== id) {
      return (
        <Tooltip
          triggerClassName={style.FolderName}
          message={name}
          messageClassName={style.FolderNameTooltip}
          side="bottom"
          theme="oneflow"
          zIndex={10002}
        >
          {folderNameElement}
        </Tooltip>
      );
    }

    return folderNameElement;
  };

  const handleClick = (folderId: Oneflow.Folder['id']) => {
    if (selectedUniqueKey !== folderId) {
      onSelect(folderId);
    }

    onFolderAddHandler();
  };

  const branchClasses = clsx(style.Branch, customClass);

  const containerClasses = clsx(style.Container, {
    [style.Active]: isCurrentFolderSelected,
  });

  const getFolderActions = () => {
    if (!isModalVersion && !isWorkspace && !isReadOnly) {
      return (
        <FolderActions
          allFolders={allFolders}
          acl={acl}
          didClickRename={didClickRename}
          folderId={id}
          folderName={name}
          folderParentId={parentId}
          onFolderAddHandler={handleClick}
          onFolderRemoveSuccess={onSelect}
          showAddFolderAction
          onMenuTriggerClick={(event) => {
            event.stopPropagation();
          }}
        />
      );
    }

    return null;
  };

  const renderEmptyState = () => {
    if (isWorkspace && !hasChildren && !isAddMode && !loading) {
      return (
        <div className={style.EmptyState}>
          {!isReadOnly && (
            <Button
              disabled={!workspaceCreatePermission}
              icon={<SmallAdd height="10px" />}
              onClick={() => handleClick(-1)}
              kind="secondary-lite"
            >
              <T
                _str="Add folder"
                _comment="Title for add folder button."
              />
            </Button>
          )}
        </div>
      );
    }

    return null;
  };

  const renderAddMode = () => {
    if (
      isAddMode &&
      (id === selectedUniqueKey ||
        // When showing all documents in the workspace (selectedUniqueKey is null and the "all documents" link is active)
        // When we're in add mode, the form needs to be shown in the workspace root folder
        (isNull(selectedUniqueKey) && id === WORKSPACE_ROOT_FOLDER_ID))
    ) {
      return (
        <div>
          <div
            className={clsx(style.Edit, {
              [style.Root]: indentationLevel === -1,
              [style.EditingError]: hasError,
            })}
          >
            <div className={style.Placeholder} />
            <div
              className={clsx(style.Icon, {
                [style.EditingError]: hasError,
              })}
            >
              <FolderIcon height="18px" />
            </div>
            <InlineEdit
              setHasError={setHasError}
              onEditFolderHandler={(value) => {
                onAddFolder(value, hasError);
              }}
              value=""
            />
          </div>
        </div>
      );
    }
    return null;
  };

  const getFolderIcon = () => {
    if (isWorkspace) {
      return (
        <div className={clsx(style.Icon, style.WorkspaceIcon)}>
          <WorkspaceFolder height="18px" />
        </div>
      );
    }

    return (
      <div
        className={clsx(style.Icon, {
          [style.EditingError]: hasError,
        })}
      >
        <FolderIcon
          height="18px"
          className={style.FolderIcon}
        />
      </div>
    );
  };

  return getBranchContent(
    <li
      className={branchClasses}
      key={id}
    >
      <Accordion.Root
        type="single"
        collapsible
        defaultValue="item-1"
        asChild
      >
        <Accordion.Item
          value="item-1"
          asChild
        >
          <div>
            <div
              className={containerClasses}
              onClick={setActiveFolder}
              onKeyDown={onKeyDownHandler}
              role="button"
              tabIndex={0}
            >
              <div
                className={clsx(style.LeftSide, {
                  [style.EditingError]: hasError,
                })}
              >
                {indentationLevel > 0 && <div className={style.Placeholder} />}
                {getFolderIcon()}
                {getFolderName()}
              </div>
              {!isEditMode && getFolderActions()}
              {folderHasChildren && (
                <Accordion.Trigger asChild>
                  <Button
                    tabIndex={-1}
                    className={clsx(style.ChildrenChevron, {
                      [style.EditingError]: hasError,
                    })}
                  >
                    <ChevronRightIcon height="10px" />
                  </Button>
                </Accordion.Trigger>
              )}
              {!isModalVersion && indentationLevel === -1 && (
                <Accordion.Trigger asChild>
                  <ChevronDownIcon
                    height="10px"
                    className={style.AccordionIcon}
                  />
                </Accordion.Trigger>
              )}
            </div>
            <Accordion.Content>
              {renderAddMode()}
              {getChildren()}
              {renderEmptyState()}
            </Accordion.Content>
          </div>
        </Accordion.Item>
      </Accordion.Root>
    </li>,
  );
};

export default FolderBranch;
